import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { AuthAPI } from '@certi/shared/api/auth/AuthAPI'
import { useAuth } from '@/hooks/useAuth'
import AuthLayout from '@/components/layout/AuthLayout'
import Button from '@/components/ui/Button'

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying')
  const [errorMessage, setErrorMessage] = useState('')
  const navigate = useNavigate()
  const { refreshUser } = useAuth()

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token')

      if (!token) {
        setStatus('error')
        setErrorMessage('Verification token is missing')
        return
      }

      try {
        await AuthAPI.verifyEmail(token)
        setStatus('success')

        // Refresh user session to update emailVerified status
        try {
          await refreshUser()
        } catch (err) {
          // Continue even if refresh fails - the verification itself succeeded
          console.error('Failed to refresh user session:', err)
        }
      } catch (err) {
        setStatus('error')
        setErrorMessage(
          err instanceof Error
            ? err.message
            : 'Invalid or expired verification link. Please request a new one.'
        )
      }
    }

    verifyEmail()
  }, [searchParams, refreshUser])

  return (
    <AuthLayout
      title=""
      subtitle=""
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center' }}>
        {status === 'verifying' && (
          <div style={{ textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto' }}></div>
            <h2 style={{
              marginTop: '16px',
              fontSize: '20px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>
              Verifying Email...
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
              Please wait while we verify your email address
            </p>
          </div>
        )}

        {status === 'success' && (
          <>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#D1FAE5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg
                className="w-8 h-8"
                style={{ color: '#10B981' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <div style={{ textAlign: 'center' }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                marginBottom: '8px'
              }}>
                Email Verified!
              </h2>
              <p style={{
                color: 'var(--text-secondary)',
                fontSize: '15px',
                lineHeight: '1.6'
              }}>
                Your email has been successfully verified
              </p>
            </div>

            <Button
              onClick={() => navigate('/profile')}
              className="w-full"
            >
              Complete Your Profile
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#FEE2E2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg
                className="w-8 h-8"
                style={{ color: '#EF4444' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>

            <div style={{ textAlign: 'center' }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                marginBottom: '8px'
              }}>
                Verification Failed
              </h2>
            </div>

            <div style={{
              backgroundColor: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: '8px',
              padding: '12px',
              width: '100%'
            }}>
              <p style={{ fontSize: '14px', color: 'var(--danger-color)', textAlign: 'center', margin: 0 }}>
                {errorMessage}
              </p>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              width: '100%',
              marginTop: '8px'
            }}>
              <Button
                onClick={() => navigate('/profile')}
                className="w-full"
              >
                Go to Profile
              </Button>

              <div style={{ textAlign: 'center' }}>
                <Link
                  to="/profile"
                  className="link-text"
                  style={{ fontSize: '14px' }}
                >
                  Request a new verification email
                </Link>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Simple spinner styles */}
      <style>{`
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #E5E7EB;
          border-top-color: #0B4D2A;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </AuthLayout>
  )
}
